/**
 * Sponsors module DTOs (§7, §8, §15.2 `/admin/sponsors`, `/admin/sampling-rules`, `/sponsor/reports`).
 * Sponsor reporting is aggregate, thresholded and sponsor-scoped (D11): never winner identity, contacts or
 * other sponsors' figures.
 */
import { z } from 'zod';
import { HexColor, HttpsUrl, IsoDateTime, LocalizedText, Slug, Ulid } from './common.js';
import { CountingMode, EntrySource, SocialPlatform, SponsorTier } from './enums.js';
import { SponsorPlacement } from './events.js';
import { RevealSkin, SamplingEligibleRange, SamplingRuleSchema } from './lottery-config.js';
import { MoneyDto } from './money.js';
export const UpsertSponsorRequest = z.object({
    sponsorId: Ulid.optional(),
    name: z.string().min(1).max(120),
    tier: SponsorTier,
    displayWeight: z.number().int().min(1).max(100).default(1),
    logoLightRef: z.string().max(512).optional(),
    logoDarkRef: z.string().max(512).optional(),
    brandColor: HexColor.optional(),
    tagline: LocalizedText.optional(),
    websiteUrl: HttpsUrl.optional(),
    socialLinks: z.array(z.object({ platform: z.string().max(20), url: HttpsUrl })).default([]),
    ctaLabel: LocalizedText.optional(),
    ctaUrl: HttpsUrl.optional(),
    productImageRefs: z.array(z.string().max(512)).default([]),
});
export const SponsorAdminDto = UpsertSponsorRequest.required({ sponsorId: true }).extend({
    tenantId: Ulid,
    /** Contrast check result for the logo/brand colour on the theme (Appendix A "Sponsor asset fails contrast"). */
    contrastOk: z.boolean(),
    viewerUserIds: z.array(Ulid),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Per-lottery sponsorship (tier, placements, prize-level sponsorship, reveal skin). */
export const UpsertLotterySponsorshipRequest = z.object({
    lotteryId: Ulid,
    sponsorId: Ulid,
    tier: SponsorTier,
    displayWeight: z.number().int().min(1).max(100).default(1),
    placements: z.array(SponsorPlacement).default([]),
    sponsoredPrizeIds: z.array(Ulid).default([]),
    revealSkin: RevealSkin.default('NONE'),
});
// --- sampling rules (§8) ------------------------------------------------------------------------------
export const UpsertSamplingRuleRequest = SamplingRuleSchema.omit({ ruleId: true, version: true }).extend({
    lotteryId: Ulid,
    ruleId: Ulid.optional(),
});
export const SamplingRuleAdminDto = SamplingRuleSchema.extend({
    lotteryId: Ulid,
    eligible: SamplingEligibleRange,
    countingMode: CountingMode,
    qualifyingEntryTypes: z.array(EntrySource),
    stock: z.object({ reserved: z.number().int(), awarded: z.number().int(), remaining: z.number().int() }),
    /** Reserved units cannot be reduced after the first qualified entry (§6.2, §8). */
    locked: z.boolean(),
    updatedAt: IsoDateTime,
});
// --- sponsor reporting (`GET /v1/sponsor/reports`) -----------------------------------------------------
/** Minimum group size below which aggregate variant preferences are suppressed (re-identification guard). */
export const SPONSOR_REPORT_SUPPRESSION_THRESHOLD = 5;
export const SponsorReportQuery = z.object({
    lotteryId: Ulid.optional(),
    from: IsoDateTime.optional(),
    to: IsoDateTime.optional(),
    format: z.enum(['JSON', 'CSV', 'PDF']).default('JSON'),
});
export const PlacementMetricsDto = z.object({
    placement: SponsorPlacement,
    viewableImpressions: z.number().int(),
    clicks: z.number().int(),
    ctr: z.number().min(0),
});
export const SponsorReportDto = z.object({
    sponsorId: Ulid,
    sponsorName: z.string(),
    generatedAt: IsoDateTime,
    window: z.object({ from: IsoDateTime, to: IsoDateTime }),
    lotteries: z.array(z.object({
        lotteryId: Ulid,
        title: z.string(),
        tier: SponsorTier,
        placements: z.array(PlacementMetricsDto),
        shareCardReach: z.object({ generated: z.number().int(), referredVisits: z.number().int() }),
        samples: z.object({
            allocated: z.number().int(),
            claimed: z.number().int(),
            delivered: z.number().int(),
            reservedRemaining: z.number().int(),
            value: MoneyDto,
        }),
        /** Aggregate variant preferences above the suppression threshold; smaller groups are omitted. */
        variantPreferences: z.array(z.object({
            axis: z.string(),
            value: z.string(),
            count: z.number().int().min(SPONSOR_REPORT_SUPPRESSION_THRESHOLD),
        })),
    })),
    exportUrl: z.string().optional(),
});
/** Keys that must never appear in a sponsor-scoped payload (tests walk the object tree). */
export const SPONSOR_PAYLOAD_FORBIDDEN_KEYS = [
    'legalName',
    'displayName',
    'contact',
    'handle',
    'handleMasked',
    'phone',
    'email',
    'idNumber',
    'payoutDestination',
    'winId',
    'entryId',
    'participantId',
];
// ---------------------------------------------------------------------------------------------------------
// Sponsors module extensions (sponsors module agent, D-1202): lists, sponsorships, viewers, events, exports.
// ---------------------------------------------------------------------------------------------------------
export const SponsorListQuery = z.object({
    q: z.string().max(80).optional(),
    tier: SponsorTier.optional(),
    active: z
        .union([z.boolean(), z.enum(['true', 'false'])])
        .transform((v) => (typeof v === 'boolean' ? v : v === 'true'))
        .optional(),
});
/** Link / unlink a SPONSOR_VIEWER staff user to a sponsor (D11 scoping). */
export const LinkSponsorViewerRequest = z.object({ userId: Ulid });
export const UpsertLotterySponsorshipRequestExt = UpsertLotterySponsorshipRequest.extend({
    /** Campaign id carried on impression / click events (§7.1). */
    campaignId: z.string().max(80).optional(),
    /** Sponsor message + CTA on loss screens (§7.1), labelled and visually secondary (§4.2 F). */
    lossMessage: LocalizedText.optional(),
    isActive: z.boolean().default(true),
});
export const LotterySponsorshipDto = z.object({
    sponsorshipId: Ulid,
    lotteryId: Ulid,
    sponsorId: Ulid,
    sponsorName: z.string(),
    tier: SponsorTier,
    displayWeight: z.number().int(),
    placements: z.array(SponsorPlacement),
    sponsoredPrizeIds: z.array(Ulid),
    revealSkin: RevealSkin,
    campaignId: z.string().nullable(),
    lossMessage: LocalizedText.nullable(),
    isActive: z.boolean(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const SponsorshipListQuery = z.object({ lotteryId: Ulid.optional(), sponsorId: Ulid.optional() });
export const SamplingRuleListQuery = z.object({ lotteryId: Ulid });
// --- sponsor events (`POST /v1/sponsor-events`, §7.1 rules) --------------------------------------------
export const SponsorEventType = z.enum(['IMPRESSION', 'CLICK']);
/**
 * One client-side placement measurement. No PII: the API derives the session hash itself. Impressions must
 * carry `viewable: true` (the viewability rule) or they are dropped and counted as rejected.
 */
export const SponsorEventInput = z.object({
    sponsorId: Ulid,
    placement: SponsorPlacement,
    eventType: SponsorEventType,
    campaignId: z.string().max(80).optional(),
    viewable: z.boolean(),
    screen: z.string().max(40).optional(),
    platform: SocialPlatform.optional(),
    clientTs: IsoDateTime.optional(),
});
export const SPONSOR_EVENT_BATCH_MAX = 50;
export const SponsorEventBatchRequest = z.object({
    /** Lottery slug: the tenant is resolved from it, never from a body tenant id. */
    slug: Slug,
    events: z.array(SponsorEventInput).min(1).max(SPONSOR_EVENT_BATCH_MAX),
});
export const SponsorEventBatchResponse = z.object({
    accepted: z.number().int(),
    rejected: z.array(z.object({ index: z.number().int(), reason: z.string() })),
});
/** Keys the sponsor-events endpoint refuses outright (no PII on measurement events, §15.1, §17.3). */
export const SPONSOR_EVENT_FORBIDDEN_KEYS = [
    'contact',
    'email',
    'phone',
    'handle',
    'participantId',
    'entryId',
    'winId',
    'token',
];
// --- sponsor report exports -------------------------------------------------------------------------------
export const SponsorReportExportDto = z.object({
    exportId: Ulid,
    sponsorId: Ulid,
    lotteryId: Ulid.nullable(),
    format: z.enum(['CSV', 'PDF']),
    generatedAt: IsoDateTime,
    expiresAt: IsoDateTime,
    sizeBytes: z.number().int(),
    /** Presigned download (short-lived); access is logged. */
    url: z.string().optional(),
});
/** Sponsor-scoped session summary for the portal header (never other sponsors). */
export const SponsorPortalMeDto = z.object({
    sponsorId: Ulid,
    sponsorName: z.string(),
    brandColor: HexColor.nullable(),
    logoLightUrl: z.string().nullable(),
    logoDarkUrl: z.string().nullable(),
    lotteries: z.array(z.object({ lotteryId: Ulid, title: z.string(), tier: SponsorTier })),
});
/** Validation issue on a saved sampling rule; `error` severity blocks publication (§8 reserved stock). */
export const SamplingRuleIssue = z.object({
    path: z.string(),
    code: z.string(),
    message: z.string(),
    severity: z.enum(['error', 'warning']),
});
export const SamplingRuleAdminDtoExt = SamplingRuleAdminDto.extend({ issues: z.array(SamplingRuleIssue) });
//# sourceMappingURL=sponsors.js.map